import React from 'react';
import { Rocket, Users, Shield, Zap, Globe, RefreshCw, Check } from 'lucide-react';

const features = [
  {
    icon: Rocket,
    title: 'Rapid Development',
    description: 'Agile methodologies and modern frameworks enable us to deliver high-quality applications faster.',
    benefits: ['Iterative development process', 'Regular milestone deliveries', 'Continuous feedback integration', 'Fast time-to-market'],
  },
  {
    icon: Users,
    title: 'User-Centric Design',
    description: 'Beautiful, intuitive interfaces designed with your users in mind for maximum engagement.',
    benefits: ['Research-driven UI/UX', 'Accessibility compliance', 'Mobile-first approach', 'User testing & optimization'],
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-grade security measures to protect your data and ensure regulatory compliance.',
    benefits: ['End-to-end encryption', 'Regular security audits', 'GDPR/CCPA compliance', 'Penetration testing'],
  },
  {
    icon: Zap,
    title: 'Peak Performance',
    description: 'Optimized code and infrastructure for lightning-fast load times and smooth operations.',
    benefits: ['Code optimization', 'CDN integration', 'Database indexing', 'Caching strategies'],
  },
  {
    icon: Globe,
    title: 'Global Scalability',
    description: 'Built to grow with your business, from startup to enterprise-level operations.',
    benefits: ['Auto-scaling infrastructure', 'Load balancing', 'Multi-region deployment', 'Microservices architecture'],
  },
  {
    icon: RefreshCw,
    title: 'Continuous Support',
    description: 'Ongoing maintenance, updates, and support to keep your application running smoothly.',
    benefits: ['24/7 monitoring', 'Regular updates', 'Bug fixes & patches', 'Performance optimization'],
  },
];

const Features = () => {
  return (
    <section className="relative py-24 md:py-32">
      <div className="frame">
        {/* Masthead */}
        <header className="grid grid-cols-12 gap-x-6 lg:gap-x-10 items-end pb-6 border-b border-ink">
          <div className="col-span-12 lg:col-span-5 flex items-center gap-3">
            <span className="section-num">04 / Why us</span>
            <span className="label-muted">What sets us apart</span>
          </div>
          <div className="col-span-12 lg:col-span-7 mt-6 lg:mt-0">
            <h2 className="display-lg">
              Why Choose <span className="text-accent">Charliecollc</span>
            </h2>
            <p className="mt-5 max-w-measure text-ink-soft leading-relaxed">
              We combine technical excellence with business acumen to deliver
              applications that drive real results.
            </p>
          </div>
        </header>

        {/* Grid */}
        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <article key={f.title} className="card-tech p-6 md:p-7 group">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-ink text-paper">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="chip-outline">{String(i + 1).padStart(2, '0')} / 06</span>
                </div>

                <h3 className="font-display font-semibold text-xl md:text-[1.5rem] tracking-tight text-ink leading-tight">
                  {f.title}
                </h3>
                <p className="mt-3 text-ink-soft text-[15px] leading-relaxed">
                  {f.description}
                </p>

                <ul className="mt-5 pt-5 border-t border-rule-soft space-y-2">
                  {f.benefits.map((b) => (
                    <li key={b} className="flex items-baseline gap-2.5 font-mono text-[12px] text-ink-soft">
                      <Check className="w-3.5 h-3.5 text-accent flex-shrink-0 translate-y-[2px]" strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* Stats strip */}
        <div className="mt-16 md:mt-20">
          <div className="flex items-baseline justify-between pb-3 border-b border-ink">
            <span className="label">By the numbers</span>
            <span className="label-muted hidden md:inline">As of 2026</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-rule-soft border border-rule-soft border-t-0">
            {[
              { n: '10+',  l: 'Years of Excellence' },
              { n: '50+',  l: 'Expert Developers' },
              { n: '200+', l: 'Happy Clients' },
              { n: '15+',  l: 'Countries Served' },
            ].map((s) => (
              <div key={s.l} className="bg-paper-2 p-6 md:p-7">
                <div className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-ink num">
                  {s.n}
                </div>
                <div className="mt-3 label">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
